import React, { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';
import BaseModal from './BaseModal';

// Har oppdatert for å kunne ta imot initialTemplateId fra "Bruk" knappen i maler
const SendContractModal = ({ isOpen, onClose, initialTemplateId }) => {
  const { contractTemplates, companies, contacts, sendContract, currentUser } = useCRM();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    template_id: '',
    company_id: '',
    contact_id: '',
    custom_message: 'Vedlagt finner du kontrakten for vår avtale. Vennligst se gjennom og signer digitalt.'
  });

  useEffect(() => {
    if (isOpen) {
      if (initialTemplateId) {
        // Hvis vi får inn en mal-ID via "Bruk"-knappen, gå direkte til steg 2
        setFormData(prev => ({ ...prev, template_id: initialTemplateId }));
        setStep(2);
      } else {
        setStep(1);
        setFormData({
          template_id: '',
          company_id: '',
          contact_id: '',
          custom_message: 'Vedlagt finner du kontrakten for vår avtale. Vennligst se gjennom og signer digitalt.'
        });
      }
    }
  }, [isOpen, initialTemplateId]);

  const selectedTemplate = useMemo(() => 
    (contractTemplates || []).find(t => t.id === formData.template_id), 
  [formData.template_id, contractTemplates]);
  
  const selectedCompany = useMemo(() => 
    (companies || []).find(c => c.company_id === formData.company_id), 
  [formData.company_id, companies]);
  
  const selectedContact = useMemo(() => 
    (contacts || []).find(c => c.contact_id === formData.contact_id), 
  [formData.contact_id, contacts]);

  const filteredContacts = useMemo(() => 
    (contacts || []).filter(c => c.company_id === formData.company_id), 
  [formData.company_id, contacts]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    let processedContent = selectedTemplate.content;
    const replacements = {
      '{{company_name}}': selectedCompany?.name || '',
      '{{contact_name}}': selectedContact?.name || '',
      '{{contact_email}}': selectedContact?.email || '',
      '{{date}}': new Date().toLocaleDateString('nb-NO'),
      '{{user_name}}': currentUser?.name || ''
    };

    Object.entries(replacements).forEach(([key, value]) => {
      processedContent = processedContent.replaceAll(key, value);
    });

    if (sendContract) {
      sendContract({
        template_id: formData.template_id,
        company_id: formData.company_id,
        contact_name: selectedContact?.name || 'Ukjent',
        processed_content: processedContent,
        message: formData.custom_message
      });
    } else {
      console.warn("sendContract is not implemented in context yet.");
      alert("Dokument sendt! (Simulert)");
    }

    onClose();
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Send Kontrakt" size="md">
      <div className="space-y-8">
        <div className="flex items-center justify-between px-4 relative">
          <div className="absolute left-8 right-8 top-1/2 -translate-y-1/2 h-[2px] bg-slate-100"></div>
          {[1, 2, 3].map((s) => (
            <div key={s} className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all border-2 ${step >= s ? 'bg-primary-600 border-primary-600 text-white shadow-lg shadow-primary-200' : 'bg-white border-slate-200 text-slate-400'}`}>
              {step > s ? <SafeIcon icon="✅" className="w-5 h-5" /> : s}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest">Velg mal</label>
                <div className="grid grid-cols-1 gap-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                  {(contractTemplates || []).map((tmpl) => (
                    <button 
                      key={tmpl.id} 
                      type="button" 
                      onClick={() => setFormData({...formData, template_id: tmpl.id})}
                      className={`flex items-center gap-4 p-4 rounded-2xl border-2 text-left transition-all ${formData.template_id === tmpl.id ? 'border-primary-600 bg-primary-50' : 'border-slate-100 hover:border-slate-200'}`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${formData.template_id === tmpl.id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <SafeIcon icon={tmpl.isLocked ? "🔒" : "📄"} className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-black text-slate-900 text-sm">{tmpl.name}</p>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">
                          Type: {tmpl.type || 'Standard'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <button 
                type="button" 
                disabled={!formData.template_id}
                onClick={() => setStep(2)}
                className="w-full py-4 bg-slate-900 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-50 transition-all uppercase tracking-widest"
              >
                Neste steg <SafeIcon icon="➡️" className="w-5 h-5" />
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <SafeIcon icon="💼" className="w-3 h-3" /> Velg Selskap
                  </label>
                  <select 
                    required
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm appearance-none"
                    value={formData.company_id}
                    onChange={(e) => setFormData({...formData, company_id: e.target.value, contact_id: ''})}
                  >
                    <option value="">Velg et selskap...</option>
                    {(companies || []).map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                    <SafeIcon icon="👤" className="w-3 h-3" /> Velg Kontaktperson
                  </label>
                  <select 
                    required
                    disabled={!formData.company_id}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold transition-all text-sm appearance-none disabled:opacity-50"
                    value={formData.contact_id}
                    onChange={(e) => setFormData({...formData, contact_id: e.target.value})}
                  >
                    <option value="">Velg en person...</option>
                    {(filteredContacts || []).map(c => <option key={c.contact_id} value={c.contact_id}>{c.name} ({c.role})</option>)}
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setStep(1)}
                  className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl hover:bg-slate-200 transition-all uppercase tracking-widest"
                >
                  Tilbake
                </button>
                <button 
                  type="button" 
                  disabled={!formData.contact_id}
                  onClick={() => setStep(3)}
                  className="flex-[2] py-4 bg-slate-900 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-50 transition-all uppercase tracking-widest"
                >
                  Neste steg <SafeIcon icon="➡️" className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
              <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-200">
                <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Oppsummering</h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-slate-500">Mal:</span>
                    <span className="font-black text-slate-900">{selectedTemplate?.name}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-slate-500">Kunde:</span>
                    <span className="font-black text-slate-900">{selectedCompany?.name}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-slate-500">Mottaker:</span>
                    <span className="font-black text-slate-900">{selectedContact?.name}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-black text-slate-500 uppercase tracking-widest">E-post melding</label>
                <textarea 
                  rows="3" 
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-medium text-sm transition-all resize-none"
                  value={formData.custom_message}
                  onChange={(e) => setFormData({...formData, custom_message: e.target.value})}
                />
              </div>

              <div className="flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setStep(2)}
                  className="flex-1 py-4 bg-slate-100 text-slate-600 font-black rounded-2xl hover:bg-slate-200 transition-all uppercase tracking-widest"
                >
                  Tilbake
                </button>
                <button 
                  type="submit" 
                  className="flex-[2] py-4 bg-primary-600 text-white font-black rounded-2xl flex items-center justify-center gap-2 hover:bg-primary-700 shadow-xl shadow-primary-100 transition-all uppercase tracking-widest"
                >
                  Send kontrakt nå <SafeIcon icon="📤" className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          )}
        </form>
      </div>
    </BaseModal>
  );
};

export default SendContractModal;